// frontend/src/components/grid/sorColumns.jsx
//
// Grid-layer decorators that project a field's Source-of-Record onto an AG Grid
// column. This lives in the grid layer (not hooks/) because it composes a React
// tooltip component; the pure SoR primitives it builds on (`isErpOwned`,
// `vendorDisplayName`) stay in hooks/useSoR so that module has no view deps.

import { isErpOwned, vendorDisplayName } from '../../hooks/useSoR';
import { makeSorAwareTooltip } from './SorAwareTooltip';

/**
 * Compose an AG-Grid column with the ERP read-only gate for `domain`. On a row
 * whose SoR for `domain` is the ERP the cell is (a) not editable, (b) shown
 * muted via the `ag-cell-disabled` class, and (c) given a hover tooltip naming
 * the owning system ("Managed in Exact Online — edit this field there"). On
 * every other row the column behaves exactly as authored: prior `editable`,
 * prior `tooltipValueGetter`, and prior `cellClassRules` are all preserved and
 * composed (never clobbered) — it can never make a hard-`false` column editable,
 * and a column's own tooltip/disabled rules still fire when the ERP isn't the
 * owner. `domain` is the row's `sor` envelope key (e.g. 'suppliers',
 * 'item_master', 'cost', 'sell_price', 'locations').
 *
 * @param {object} colDef  AG-Grid column definition
 * @param {string} domain
 * @param {(key: string, opts?: object) => string} [t]  i18next translator; when
 *   omitted the ERP-locked tooltip is suppressed (the lock + muting still apply).
 * @returns {object} a new colDef with composed editable / tooltip / class rules
 */
export function withSorReadOnly(colDef, domain, t) {
  const prevEditable = colDef.editable;
  const prevTooltip = colDef.tooltipValueGetter;
  const prevClassRules = colDef.cellClassRules || {};
  const prevDisabledRule = prevClassRules['ag-cell-disabled'];

  const domainSor = (params) => params?.data?.sor?.[domain];

  return {
    ...colDef,
    // A column-level custom tooltip (e.g. the promotion tooltip on the price
    // columns) also fires on an ERP-owned row, where it would swallow the
    // read-only message below. Wrap it so ERP rows render the message and every
    // other row delegates unchanged. Columns with no custom component fall
    // through to AG Grid's default text tooltip, which renders the message as-is.
    ...(colDef.tooltipComponent
      ? { tooltipComponent: makeSorAwareTooltip(colDef.tooltipComponent, domain) }
      : {}),
    editable: (params) => {
      const base = typeof prevEditable === 'function' ? prevEditable(params) : prevEditable !== false;
      return base && !isErpOwned(domainSor(params));
    },
    tooltipValueGetter: (params) => {
      const sor = domainSor(params);
      if (isErpOwned(sor)) {
        return t ? t('sor.erpManagedField', { vendor: vendorDisplayName(sor.vendor) }) : '';
      }
      return typeof prevTooltip === 'function' ? prevTooltip(params) : '';
    },
    cellClassRules: {
      ...prevClassRules,
      // Compose (not replace) any disabled rule the column authored itself.
      'ag-cell-disabled': (params) => {
        const prevDisabled = typeof prevDisabledRule === 'function'
          ? prevDisabledRule(params)
          : !!prevDisabledRule;
        return prevDisabled || isErpOwned(domainSor(params));
      },
    },
  };
}
